import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faDownload, 
    faShareAlt,
    faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, setDoc } from 'firebase/firestore';

/**
 * Reusable component for quilt action buttons (download, share)
 * Modified to work with Canvas-based quilt rendering with improved fallbacks
 */
const QuiltActionButtons = ({ 
    canvasRef, 
    showBinding,
    showBorder,
    bindingColor, 
    borderColor,
    embossedItems,
    textItems,
    currentUser,
    storage,
    db,
    setShowModal,
    setShareLink
}) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const [isSharing, setIsSharing] = useState(false);
    const [localCanvas, setLocalCanvas] = useState(null);
    
    // New: Add an effect to find the canvas on mount and store it
    useEffect(() => {
        // Try to find and store the canvas reference as soon as the component mounts
        const findAndStoreCanvas = () => {
            // First try the provided canvasRef
            if (canvasRef && canvasRef.current) {
                console.log('Found canvas from provided reference on mount');
                setLocalCanvas(canvasRef.current);
                return;
            }
            
            // Otherwise search the DOM
            const domCanvas = findCanvasInDOM();
            if (domCanvas) {
                console.log('Found canvas in DOM on mount');
                setLocalCanvas(domCanvas);
            }
        };
        
        // Run immediately
        findAndStoreCanvas();
        
        // And also set a short delay to try again (canvas might not be ready immediately)
        const retryTimer = setTimeout(findAndStoreCanvas, 500);
        
        return () => clearTimeout(retryTimer);
    }, [canvasRef]);
    
    // Enhanced canvas finder to look specifically for quilt-related canvases
    const findCanvasInDOM = () => {
        console.log('Looking for canvas in DOM...');
        
        // Specific selectors to try, in order of preference
        const candidateSelectors = [
            // First try explicit references
            'canvas.quilt-canvas',             // Class-based
            '#quilt-canvas',                   // ID-based
            'canvas.canvas-quilt-renderer',    // CanvasQuiltRenderer canvas
            '#step3-canvas',                   // Step3 canvas
            '.canvas-quilt-renderer canvas',   // Canvas inside renderer container
            '.quilt-display canvas',           // Canvas inside QuiltDisplay
            
            // Step 6 specific canvas - more specific selectors first
            'canvas[data-quilt-canvas="main"]',
            '#enhance-layout-canvas',
            '.canvas-container canvas',
            '.quilt-preview canvas',
            
            // More general selectors
            'canvas[data-quilt="true"]',
            'div[id*="quilt"] canvas',
            'div[class*="quilt"] canvas',
            
            // Last resort - any canvas
            'canvas'
        ];
        
        // Try each selector in order
        for (const selector of candidateSelectors) {
            // Try both querySelector (first match) and querySelectorAll (all matches)
            const singleCanvas = document.querySelector(selector);
            if (singleCanvas && isValidCanvas(singleCanvas)) {
                console.log(`Found canvas with selector: ${selector}`);
                return singleCanvas;
            }
            
            // If single query didn't work, try getting all and filtering
            const allMatches = document.querySelectorAll(selector);
            if (allMatches.length > 0) {
                // Filter to valid, visible canvases
                const validCanvases = Array.from(allMatches).filter(c => isValidCanvas(c));
                if (validCanvases.length > 0) {
                    console.log(`Found ${validCanvases.length} canvases with selector: ${selector}`);
                    
                    // Sort by size (largest first) and return the largest one
                    validCanvases.sort((a, b) => (b.width * b.height) - (a.width * a.height));
                    return validCanvases[0];
                }
            }
        }
        
        // If no canvas found with selectors, look for one in elements that seem quilt-related
        const quiltContainers = [
            ...document.querySelectorAll('[id*="quilt"],[class*="quilt"],[id*="canvas"],[class*="canvas"]')
        ];
        
        for (const container of quiltContainers) {
            const canvasesInContainer = container.querySelectorAll('canvas');
            if (canvasesInContainer.length > 0) {
                const validCanvases = Array.from(canvasesInContainer).filter(c => isValidCanvas(c));
                if (validCanvases.length > 0) {
                    console.log(`Found ${validCanvases.length} canvases in container: ${container.className || container.id}`);
                    validCanvases.sort((a, b) => (b.width * b.height) - (a.width * a.height));
                    return validCanvases[0];
                }
            }
        }
        
        // Last resort - get all canvases and try to find the most likely one
        const allCanvases = document.querySelectorAll('canvas');
        if (allCanvases.length > 0) {
            const validCanvases = Array.from(allCanvases).filter(c => isValidCanvas(c));
            if (validCanvases.length > 0) {
                console.log(`Found ${validCanvases.length} total canvases on page`);
                
                // Sort by size (largest first) and return the largest one
                validCanvases.sort((a, b) => (b.width * b.height) - (a.width * a.height));
                
                // Extra check - if largest canvas is much bigger than others, it's likely our quilt
                if (validCanvases.length > 1) {
                    const largestArea = validCanvases[0].width * validCanvases[0].height;
                    const secondArea = validCanvases[1].width * validCanvases[1].height;
                    
                    // If largest is at least 30% bigger than second largest, it's likely our quilt
                    if (largestArea > secondArea * 1.3) {
                        console.log('Found largest canvas that is significantly larger than others');
                        return validCanvases[0];
                    }
                } else {
                    return validCanvases[0]; // Only one canvas
                }
            }
        }
        
        console.log('No suitable canvas found in DOM');
        return null;
    };
    
    // Helper to check if a canvas is valid and visible
    const isValidCanvas = (canvas) => {
        // Check if it's a valid canvas element
        if (!canvas || typeof canvas.getContext !== 'function') {
            return false;
        }
        
        try {
            // Check if the canvas has dimensions
            if (canvas.width <= 10 || canvas.height <= 10) {
                return false;
            }
            
            // Check if the canvas is visible
            const style = window.getComputedStyle(canvas);
            if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
                return false;
            }
            
            // Check for reasonable minimum size to exclude tiny canvases
            if (parseInt(style.width) < 100 || parseInt(style.height) < 100) {
                return false;
            }
            
            return true;
        } catch (e) {
            return false;
        }
    };
    
    // Attempt to dynamically create a screenshot if no canvas is available
    const createScreenshot = async () => {
        // Find the quilt container
        const quiltContainer = document.querySelector(
            '.quilt-display, .canvas-quilt-renderer, [class*="quilt-container"], [id*="quilt-container"]'
        );
        
        if (!quiltContainer) {
            throw new Error("Could not find quilt container for screenshot");
        }
        
        try {
            // Use html2canvas library if available
            if (typeof window.html2canvas === 'function') {
                const canvas = await window.html2canvas(quiltContainer, {
                    backgroundColor: '#FFFFFF',
                    scale: 2,
                    useCORS: true,
                    allowTaint: true,
                    logging: false
                });
                return canvas;
            } else {
                throw new Error("html2canvas not available");
            }
        } catch (error) {
            console.error("Error creating screenshot:", error);
            throw new Error("Could not create screenshot");
        }
    };
    
    // Download function - gets the image directly from the canvas with enhanced logic
    const handleDownloadClick = async () => {
        setIsDownloading(true);
        
        try {
            // IMPORTANT: Force a small delay to ensure canvas is updated with latest colors
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Create file name with timestamp
            const timestamp = new Date().toISOString().replace(/:/g, '-');
            const fileName = `quilt_${timestamp}.png`;
            
            // Try multiple methods to get a valid canvas
            let canvas = null;
            
            // Method 1: Always refresh canvas reference to get the most current one
            canvas = findCanvasInDOM();
            if (canvas) {
                console.log('Found fresh canvas in DOM with current rendering');
                setLocalCanvas(canvas); // Store for future use
            }
            // Method 2: Try using the stored local canvas only as fallback
            else if (localCanvas) {
                console.log('Using stored local canvas reference as fallback');
                canvas = localCanvas;
            }
            // Method 3: Try using the provided canvasRef as last resort
            else if (canvasRef && canvasRef.current) {
                console.log('Using canvas from provided reference as last resort');
                canvas = canvasRef.current;
                setLocalCanvas(canvas); // Store for future use
            }
            
            // If we found a canvas, verify it has current content before downloading
            if (canvas) {
                try {
                    // Verify the canvas has been rendered recently
                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        throw new Error("Could not get canvas context");
                    }
                    
                    // Check if canvas appears to have content (not blank)
                    const imageData = ctx.getImageData(0, 0, Math.min(canvas.width, 10), Math.min(canvas.height, 10));
                    const hasContent = imageData.data.some((value, index) => {
                        // Check if any pixel is not white (not rgba(255,255,255,255))
                        if (index % 4 === 3) return false; // Skip alpha channel
                        return value !== 255;
                    });
                    
                    if (!hasContent) {
                        console.warn("Canvas appears to be blank, forcing re-render");
                        // Give more time for rendering
                        await new Promise(resolve => setTimeout(resolve, 1000));
                    }
                    
                    // IMPROVED: Try to convert canvas to blob with maximum quality
                    canvas.toBlob((blob) => {
                        if (!blob) {
                            throw new Error("Could not create image blob");
                        }
                        
                        const url = URL.createObjectURL(blob);
                        const downloadLink = document.createElement('a');
                        downloadLink.href = url;
                        downloadLink.download = fileName;
                        document.body.appendChild(downloadLink);
                        downloadLink.click();
                        document.body.removeChild(downloadLink);
                        URL.revokeObjectURL(url);
                        setIsDownloading(false);
                    }, 'image/png', 1.0); // Use PNG for lossless quality
                } catch (blobError) {
                    console.error("Error creating blob:", blobError);
                    
                    // Fallback to dataURL if blob fails
                    try {
                        const dataUrl = canvas.toDataURL('image/png', 1.0); // Maximum quality PNG
                        const downloadLink = document.createElement('a');
                        downloadLink.href = dataUrl;
                        downloadLink.download = fileName;
                        document.body.appendChild(downloadLink);
                        downloadLink.click();
                        document.body.removeChild(downloadLink);
                    } catch (dataUrlError) {
                        console.error("DataURL fallback failed:", dataUrlError);
                        throw new Error("Could not generate download from canvas");
                    }
                }
            } 
            // Method 4: Try to create a screenshot
            else {
                try {
                    console.log("No canvas found, attempting to create screenshot");
                    const screenshotCanvas = await createScreenshot();
                    if (screenshotCanvas) {
                        console.log("Screenshot created successfully");
                        setLocalCanvas(screenshotCanvas); // Store for future use
                        
                        const dataUrl = screenshotCanvas.toDataURL('image/png');
                        const downloadLink = document.createElement('a');
                        downloadLink.href = dataUrl;
                        downloadLink.download = fileName;
                        document.body.appendChild(downloadLink);
                        downloadLink.click();
                        document.body.removeChild(downloadLink);
                    } else {
                        throw new Error("Could not create screenshot");
                    }
                } catch (screenshotError) {
                    console.error("Screenshot creation failed:", screenshotError);
                    // As last resort, show the manual screenshot helper
                    handleManualScreenshot();
                }
            }
        } catch (error) {
            console.error("Error downloading quilt:", error);
            alert("We couldn't automatically download your quilt. Please use the Screenshot Help option.");
            handleManualScreenshot();
        } finally {
            setIsDownloading(false);
        }
    };
    
    // Share function - shares the image directly from the canvas
    const handleShareClick = async () => {
        setIsSharing(true);
        
        try {
            if (!currentUser || !storage || !db) {
                throw new Error("Missing authentication or storage access");
            }
            
            // Show the share modal early (optimistic UI)
            if (setShowModal) {
                setShowModal(true);
            }
            
            // IMPORTANT: Force a small delay to ensure canvas is updated with latest colors
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Try multiple methods to get a valid canvas
            let canvas = null;
            
            // Method 1: Always refresh canvas reference to get the most current one
            canvas = findCanvasInDOM();
            if (canvas) {
                console.log('Found fresh canvas in DOM with current rendering for sharing');
                setLocalCanvas(canvas); // Store for future use
            }
            // Method 2: Try using the stored local canvas only as fallback
            else if (localCanvas) {
                console.log('Using stored local canvas reference for sharing as fallback');
                canvas = localCanvas;
            }
            // Method 3: Try using the provided canvasRef as last resort
            else if (canvasRef && canvasRef.current) {
                console.log('Using canvas from provided reference for sharing as last resort');
                canvas = canvasRef.current;
                setLocalCanvas(canvas); // Store for future use
            }
            
            // If no canvas found yet, try screenshot
            if (!canvas) {
                try {
                    console.log("No canvas found, attempting to create screenshot");
                    canvas = await createScreenshot();
                    if (canvas) {
                        console.log("Screenshot created successfully");
                        setLocalCanvas(canvas); // Store for future use
                    } else {
                        throw new Error("Could not create screenshot");
                    }
                } catch (screenshotError) {
                    console.error("Screenshot creation failed:", screenshotError);
                    throw new Error("Could not find or create canvas for sharing");
                }
            }
            
            // If we still don't have a canvas, handle the error
            if (!canvas) {
                throw new Error("Could not find canvas for sharing");
            }
            
            // Generate a unique ID for this shared quilt
            const designId = `${currentUser.uid}-${Date.now()}`;
            
            // Convert canvas to blob for upload
            try {
                canvas.toBlob(async (blob) => {
                    if (!blob) {
                        throw new Error("Could not create image blob for sharing");
                    }
                    
                    try {
                        // Upload to Firebase Storage
                        const storageRef = ref(storage, `user_quilts/${currentUser.uid}/${designId}.jpg`);
                        await uploadBytes(storageRef, blob);
                        const publicUrl = await getDownloadURL(storageRef);
                        
                        // Save to Firestore
                        await setDoc(doc(db, "shared_quilts", designId), {
                            quiltImageUrl: publicUrl,
                            userId: currentUser.uid,
                            createdAt: new Date(),
                            hasBinding: showBinding,
                            bindingColor: bindingColor,
                            hasBorder: showBorder,
                            borderColor: borderColor,
                            embossedItemCount: embossedItems?.length || 0,
                            textItemCount: textItems?.length || 0,
                            isCustomized: true
                        });
                        
                        // Create shareable link
                        const shareableLink = `https://nostai.com/c/${designId}`;
                        
                        // Update share link in parent component
                        if (setShareLink) {
                            setShareLink(shareableLink);
                        }
                        
                        console.log("Quilt shared successfully:", shareableLink);
                        
                    } catch (uploadError) {
                        console.error("Error uploading or saving quilt:", uploadError);
                        
                        // Update share link with error message
                        if (setShareLink) {
                            setShareLink("Error: " + uploadError.message);
                        }
                    } finally {
                        setIsSharing(false);
                    }
                }, 'image/jpeg', 0.9);
            } catch (error) {
                console.error("Error preparing quilt for sharing:", error);
                
                // Fallback to dataURL conversion if blob fails
                try {
                    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
                    
                    // Convert dataURL to blob manually
                    const byteString = atob(dataUrl.split(',')[1]);
                    const mimeType = dataUrl.split(',')[0].split(':')[1].split(';')[0];
                    const ab = new ArrayBuffer(byteString.length);
                    const ia = new Uint8Array(ab);
                    
                    for (let i = 0; i < byteString.length; i++) {
                        ia[i] = byteString.charCodeAt(i);
                    }
                    
                    const blob = new Blob([ab], { type: mimeType });
                    
                    // Upload to Firebase Storage
                    const storageRef = ref(storage, `user_quilts/${currentUser.uid}/${designId}.jpg`);
                    await uploadBytes(storageRef, blob);
                    const publicUrl = await getDownloadURL(storageRef);
                    
                    // Save to Firestore
                    await setDoc(doc(db, "shared_quilts", designId), {
                        quiltImageUrl: publicUrl,
                        userId: currentUser.uid,
                        createdAt: new Date(),
                        hasBinding: showBinding,
                        bindingColor: bindingColor,
                        hasBorder: showBorder,
                        borderColor: borderColor,
                        embossedItemCount: embossedItems?.length || 0,
                        textItemCount: textItems?.length || 0,
                        isCustomized: true
                    });
                    
                    // Create shareable link
                    const shareableLink = `https://nostai.com/c/${designId}`;
                    
                    // Update share link in parent component
                    if (setShareLink) {
                        setShareLink(shareableLink);
                    }
                    
                    console.log("Quilt shared successfully using dataURL fallback:", shareableLink);
                } catch (fallbackError) {
                    console.error("Error with dataURL fallback:", fallbackError);
                    throw fallbackError;
                }
            }
        } catch (error) {
            console.error("Error sharing quilt:", error);
            
            // Update share link with error message
            if (setShareLink) {
                setShareLink("Error: " + error.message);
            }
            
            setIsSharing(false);
            alert("Failed to share the quilt. Please try the Screenshot Help option.");
        }
    };
    
    // Manual screenshot helper function (enhanced)
    const handleManualScreenshot = async () => {
        // Create a centered modal to help user take a manual screenshot
        const modal = document.createElement('div');
        modal.style.position = 'fixed';
        modal.style.top = '0';
        modal.style.left = '0';
        modal.style.width = '100%';
        modal.style.height = '100%';
        modal.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
        modal.style.display = 'flex';
        modal.style.flexDirection = 'column';
        modal.style.justifyContent = 'center';
        modal.style.alignItems = 'center';
        modal.style.zIndex = '10000';
        modal.style.padding = '20px';
        
        // Add instructions
        const instructions = document.createElement('div');
        instructions.style.backgroundColor = 'white';
        instructions.style.padding = '20px';
        instructions.style.borderRadius = '8px';
        instructions.style.maxWidth = '500px';
        instructions.style.textAlign = 'center';
        
        instructions.innerHTML = `
            <h3 style="font-size: 20px; margin-bottom: 16px;">Take a Screenshot Manually</h3>
            <p style="margin-bottom: 16px;">Automatic capture failed. Please take a screenshot manually:</p>
            <ol style="text-align: left; margin-bottom: 16px;">
                <li style="margin-bottom: 8px;">On Windows: Press <strong>Windows+Shift+S</strong> to open the screenshot tool</li>
                <li style="margin-bottom: 8px;">On Mac: Press <strong>Command+Shift+4</strong> to capture a portion of the screen</li>
                <li style="margin-bottom: 8px;">On Mobile: Use your device's screenshot feature</li>
            </ol>
            <p style="margin-bottom: 16px;">After taking the screenshot, you can share it or save it to your device.</p>
            <button id="close-screenshot-modal" style="background-color: #5B7A8C; color: white; border: none; padding: 10px 20px; border-radius: 4px; cursor: pointer;">Close</button>
        `;
        
        modal.appendChild(instructions);
        document.body.appendChild(modal);
        
        // Add event listener to close button
        document.getElementById('close-screenshot-modal').addEventListener('click', () => {
            document.body.removeChild(modal);
        });
    };
    
    const handleDownload = async () => {
        if (!canvasRef?.current) {
            console.error('Canvas reference not available for download');
            return;
        }

        try {
            // Get the canvas element
            const canvas = canvasRef.current;
            
            // Get actual dimensions from the canvas
            const canvasWidth = canvas.width;
            const canvasHeight = canvas.height;
            
            // Create a high-resolution download canvas
            const downloadCanvas = document.createElement('canvas');
            const downloadCtx = downloadCanvas.getContext('2d');
            
            // Use higher resolution for download (2x scaling)
            const scale = 2;
            downloadCanvas.width = canvasWidth * scale;
            downloadCanvas.height = canvasHeight * scale;
            
            // Scale the context to maintain quality
            downloadCtx.scale(scale, scale);
            
            // Draw the original canvas onto the download canvas
            downloadCtx.drawImage(canvas, 0, 0);
            
            // Add watermark in the binding/border area if present
            const watermarkHeight = 30;
            downloadCtx.font = `${12 * scale}px Arial`;
            downloadCtx.fillStyle = 'rgba(0, 0, 0, 0.7)';
            downloadCtx.textAlign = 'right';
            
            // Position watermark in the bottom-right corner
            const watermarkX = (canvasWidth - 20);
            const watermarkY = (canvasHeight - 10);
            downloadCtx.fillText('Generated with NOSTAI', watermarkX, watermarkY);
            
            // Create download link
            const currentDate = new Date();
            const formattedDate = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1)
                .toString()
                .padStart(2, '0')}-${currentDate.getDate().toString().padStart(2, '0')}`;
            
            const fileName = `NOSTAI_Enhanced_Quilt_${formattedDate}.png`;
            
            // Trigger download
            const link = document.createElement('a');
            link.href = downloadCanvas.toDataURL('image/png');
            link.download = fileName;
            link.click();
            
            console.log('Enhanced quilt downloaded successfully');
        } catch (error) {
            console.error('Error downloading enhanced quilt:', error);
        }
    };

    return (
        <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-4 mt-3">
            <button
                onClick={handleDownloadClick}
                disabled={isDownloading}
                className={`bg-[#2D8B47] hover:bg-[#256E39] disabled:bg-gray-400 text-white py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg flex items-center text-sm sm:text-base`}
            >
                {isDownloading ? (
                    <>
                        <FontAwesomeIcon icon={faSpinner} spin className="mr-1 sm:mr-2" />
                        <span>Downloading...</span>
                    </>
                ) : (
                    <>
                        <FontAwesomeIcon icon={faDownload} className="mr-1 sm:mr-2" />
                        <span>Download</span>
                    </>
                )}
            </button>
            
            <button
                onClick={handleShareClick}
                disabled={isSharing}
                className={`bg-[#5B7A8C] hover:bg-[#496779] disabled:bg-gray-400 text-white py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg flex items-center text-sm sm:text-base`}
            >
                {isSharing ? (
                    <>
                        <FontAwesomeIcon icon={faSpinner} spin className="mr-1 sm:mr-2" />
                        <span>Sharing...</span>
                    </>
                ) : (
                    <>
                        <FontAwesomeIcon icon={faShareAlt} className="mr-1 sm:mr-2" />
                        <span>Share</span>
                    </>
                )}
            </button>
        </div>
    );
};

export default QuiltActionButtons;
